import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { MessageCircle, MessageSquare, HelpCircle, Compass } from "lucide-react";
import AdminPinGate from "../components/AdminPinGate";
import { hasAdminPin, isAdminUnlocked } from "../lib/kioskConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_LABEL = { en: "English", hi: "हिन्दी", mr: "मराठी", te: "తెలుగు", unknown: "Other" };
const TRAIT_LABEL = {
  R: "Doer", I: "Thinker", A: "Creator", S: "Helper", E: "Leader", C: "Organiser",
};
const COLOR = ["#C85A40", "#E69E22", "#2B8C67", "#8B5CF6", "#0EA5E9", "#E11D48"];

function Metric({ icon, label, value, tone, testid }) {
  return (
    <div className={`rounded-3xl p-6 border border-border shadow-sm bg-white`} data-testid={testid}>
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${tone} mb-4`}>
        {icon}
      </div>
      <div className="text-4xl font-bold text-foreground tracking-tight">{value}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function Admin() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !hasAdminPin() || isAdminUnlocked());
  const [gateOpen, setGateOpen] = useState(() => hasAdminPin() && !isAdminUnlocked());

  useEffect(() => {
    if (!unlocked) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [unlocked]);

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center">
        <p className="text-muted-foreground">{t("pin_lock")}</p>
        <AdminPinGate
          open={gateOpen}
          onOpenChange={(v) => { setGateOpen(v); if (!v && !isAdminUnlocked()) navigate("/"); }}
          onUnlocked={() => { setUnlocked(true); setGateOpen(false); }}
        />
      </div>
    );
  }

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-10 text-muted-foreground">Loading...</div>;
  }
  if (!stats) {
    return <div className="max-w-6xl mx-auto px-6 py-10 text-muted-foreground">{t("no_data")}</div>;
  }

  const dailyData = (stats.daily || []).map((d) => ({
    date: d.date.slice(5),  // MM-DD
    Conversations: d.sessions,
    Messages: d.messages,
  }));

  const langData = (stats.by_language || []).map((l) => ({
    name: LANG_LABEL[l.language] || l.language || "Other",
    value: l.count,
  }));

  const traitData = (stats.traits || []).map((tr) => ({
    name: TRAIT_LABEL[tr.trait] || tr.trait,
    count: tr.count,
  }));

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{t("dashboard_title")}</h1>
        <p className="text-muted-foreground mt-2">{t("dashboard_sub")}</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Metric
          testid="metric-conversations"
          icon={<MessageCircle className="w-6 h-6 text-white" strokeWidth={2.5} />}
          label={t("metric_conversations")}
          value={stats.total_sessions}
          tone="bg-primary"
        />
        <Metric
          testid="metric-messages"
          icon={<MessageSquare className="w-6 h-6 text-white" strokeWidth={2.5} />}
          label={t("metric_messages")}
          value={stats.total_messages}
          tone="bg-accent"
        />
        <Metric
          testid="metric-user-msgs"
          icon={<HelpCircle className="w-6 h-6 text-foreground" strokeWidth={2.5} />}
          label={t("metric_user_msgs")}
          value={stats.total_user_messages}
          tone="bg-secondary"
        />
        <Metric
          testid="metric-quizzes"
          icon={<Compass className="w-6 h-6 text-white" strokeWidth={2.5} />}
          label={t("metric_quizzes")}
          value={stats.total_quizzes}
          tone="bg-[#8B5CF6]"
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-border p-6" data-testid="chart-daily">
          <h3 className="text-lg font-bold text-foreground mb-4">{t("chart_daily")}</h3>
          {dailyData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dailyData}>
                <XAxis dataKey="date" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(200,90,64,0.06)" }} />
                <Legend />
                <Bar dataKey="Conversations" fill="#C85A40" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Messages" fill="#2B8C67" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-muted-foreground py-16 text-center">{t("no_data")}</div>
          )}
        </div>

        <div className="rounded-3xl bg-white border border-border p-6" data-testid="chart-lang">
          <h3 className="text-lg font-bold text-foreground mb-4">{t("chart_lang")}</h3>
          {langData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={langData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
                  {langData.map((_, i) => <Cell key={i} fill={COLOR[i % COLOR.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-muted-foreground py-16 text-center">{t("no_data")}</div>
          )}
        </div>

        <div className="lg:col-span-2 rounded-3xl bg-white border border-border p-6" data-testid="chart-traits">
          <h3 className="text-lg font-bold text-foreground mb-4">{t("chart_traits")}</h3>
          {traitData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={traitData} layout="vertical">
                <XAxis type="number" stroke="#6B7280" fontSize={12} allowDecimals={false} />
                <YAxis dataKey="name" type="category" stroke="#6B7280" fontSize={12} width={90} />
                <Tooltip cursor={{ fill: "rgba(200,90,64,0.06)" }} />
                <Bar dataKey="count" fill="#E69E22" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-muted-foreground py-16 text-center">{t("no_data")}</div>
          )}
        </div>

        <div className="rounded-3xl bg-white border border-border p-6" data-testid="recent-questions">
          <h3 className="text-lg font-bold text-foreground mb-4">{t("recent_questions")}</h3>
          <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
            {(stats.recent_questions || []).length === 0 && (
              <div className="text-sm text-muted-foreground">{t("no_data")}</div>
            )}
            {(stats.recent_questions || []).map((q, i) => (
              <div key={i} className="text-sm bg-secondary/15 border border-border rounded-xl px-3 py-2">
                <div className="text-foreground">{q.text}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {LANG_LABEL[q.language] || q.language || "—"}
                  {" · "}
                  {q.created_at ? new Date(q.created_at).toLocaleString() : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
